import { useMemo } from 'react';

import { TOKEN_SCOPE, useServiceAppState, useUserInfoState } from '@kc-console/context';

import type { ResponseCloudRole, TokenScope } from '@kc-console/context';
import type { RequiredBy } from '@kc-console/core';

const ProjectScopeAllowedRoles = ['admin', 'member'];

const ProjectScopeReaderRoles = ['reader'];

const DomainScopeAllowedRoles = ['admin'];

const DomainScopeReaderRoles = ['reader'];

type UseCheckReaderRequest = {
  allowedRoles?: {
    [TOKEN_SCOPE.DOMAIN]?: string[];
    [TOKEN_SCOPE.PROJECT]?: string[];
  };
  readerRoles?: {
    [TOKEN_SCOPE.DOMAIN]?: string[];
    [TOKEN_SCOPE.PROJECT]?: string[];
  };
};

type UseCheckReaderResponse = {
  isReader: boolean;
  isUnauthorized: boolean;
  roles: string[];
  scope: TokenScope;
};

/**
 * Reader 권한을 확인할 수 있는 훅입니다.
 * @description
 * - userInfo의 roles에서 reader 권한만 가지고 있는지 확인합니다.
 * - isReader는 readerRoles에는 포함되지만 allowedRoles에는 포함되지 않을 때 true입니다.
 * - isUnauthorized는 allowedRoles에 포함되는 role이 없을 때 true입니다.
 * - 현재 scope(DOMAIN 또는 PROJECT)에 따라 다른 기본 roles를 적용합니다.
 * - 기본 readerRoles: DOMAIN ['reader'], PROJECT ['reader']
 * - 기본 allowedRoles: DOMAIN ['admin'], PROJECT ['admin', 'member']
 * - request를 통해 scope별 커스텀 readerRoles 및 allowedRoles를 제공할 수 있습니다.
 * - userInfo가 없을 경우 isReader: false, isUnauthorized: true를 반환합니다.
 * - 모든 role name은 소문자로 변환하여 비교합니다.
 * @example
 * // 기본 사용법
 * const { isReader, isUnauthorized, roles, scope } = useCheckReader();
 * if (isReader) {
 *   // reader 권한만 있는 경우 (admin, member 등의 상위 권한은 없음)
 * }
 *
 * @example
 * // 커스텀 reader/allowed roles 지정
 * const { isReader } = useCheckReader({
 *   readerRoles: {
 *     [TOKEN_SCOPE.DOMAIN]: ['reader', 'viewer'],
 *     [TOKEN_SCOPE.PROJECT]: ['reader', 'viewer'],
 *   },
 *   allowedRoles: {
 *     [TOKEN_SCOPE.DOMAIN]: ['admin', 'super-admin'],
 *     [TOKEN_SCOPE.PROJECT]: ['admin', 'member', 'owner'],
 *   },
 * });
 */
const useCheckReader = (request?: UseCheckReaderRequest) => {
  const { allowedRoles = {}, readerRoles = {} } = request || {};
  const {
    state: { scope },
  } = useServiceAppState();

  const result: UseCheckReaderResponse = {
    isReader: false,
    isUnauthorized: true,
    scope,
    roles: [],
  };

  const { data } = useUserInfoState();

  const allowedRolesByScope = useMemo(() => {
    if (scope === TOKEN_SCOPE.DOMAIN) {
      return allowedRoles[TOKEN_SCOPE.DOMAIN] || DomainScopeAllowedRoles;
    }
    return allowedRoles[TOKEN_SCOPE.PROJECT] || ProjectScopeAllowedRoles;
  }, [allowedRoles, scope]);

  const readerRolesByScope = useMemo(() => {
    if (scope === TOKEN_SCOPE.DOMAIN) {
      return readerRoles[TOKEN_SCOPE.DOMAIN] || DomainScopeReaderRoles;
    }
    return readerRoles[TOKEN_SCOPE.PROJECT] || ProjectScopeReaderRoles;
  }, [readerRoles, scope]);

  if (!data?.roles) return result;
  const { roles } = data;

  const reDefineRoles = (
    roles.filter(role => !!role?.name) as RequiredBy<ResponseCloudRole, 'name'>[]
  ).map(role => ({
    ...role,
    name: role.name.toLowerCase(),
  }));
  const reDefinedRoleNames: string[] = reDefineRoles.map(role => role.name);

  const hasReaderRole = reDefinedRoleNames.some(roleName => readerRolesByScope.includes(roleName));
  const hasAllowedRole = reDefinedRoleNames.some(roleName =>
    allowedRolesByScope.includes(roleName),
  );

  return {
    ...result,
    isReader: hasReaderRole && !hasAllowedRole,
    isUnauthorized: !hasAllowedRole,
    roles: reDefinedRoleNames,
    scope,
  };
};

export default useCheckReader;
